import * as React from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import styled from "styled-components";
import { Flex, Button, ButtonClass, Loading } from "src/components/ui";
import closeIcon from "src/assets/images/common/closeIcon.svg";
import { theme } from "src/styles/Theme";
import {
	usePushNotificationMutation,
	useUploadImageMutation,
} from "src/store/services/adminApi";
import { toast, ToastContainer } from "react-toastify";
import { FilterButton } from "src/styles/commonStyle";
import filterIcon from "src/assets/images/common/filter.svg";
import FilterNotificationModal from "./FilterNotificationModal";

const Wrapper = styled.div`
	.heading {
		background-color: ${(props) => props.theme.colors.gray_04};
		padding: 10px 20px;
		height: 56px;
		align-items: center;
		display: flex;
		border-radius: 8px 8px 0 0;

		img {
			padding: 8px;
			&:hover {
				cursor: pointer;
				background-color: ${(props) => props.theme.colors.lilac};
				transition: 0.3s;
				border-radius: 50%;
			}
		}

		p {
			font-weight: 700;
			font-size: 16px;
		}
	}

	.content {
		padding: 20px;

		.label {
			font-size: 14px;
			margin-bottom: 5px;
		}

		input,
		textarea {
			padding: 10px 14px;
			border: 1px solid ${(props) => props.theme.colors.text_02};
			border-radius: 8px;
			width: 100%;
			outline: none;
		}

		textarea {
			height: 235px;
			resize: none;
		}

		.placeholder-buttons {
			margin-bottom: 10px;

			button {
				padding: 6px 10px;
				margin-right: 6px;
				border-radius: 6px;
				border: 1px solid #ccc;
				background: #f5f5f5;
				cursor: pointer;

				&:hover {
					background: #eaeaea;
				}
			}
		}

		.error {
			color: red;
			font-size: 12px;
		}
	}
`;

const style = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 532,
	maxWidth: "90%",
	bgcolor: theme.colors.white,
	borderRadius: "8px",
};

export const SendNotificationModal: React.FC<any> = ({
	open,
	handleClose,
	userId,
	artisan,
}) => {
	const [notificationPayload, setNotificationPayload] = React.useState({
		title: "",
		body: "",
		bodyError: "",
		titleError: "",
	});

	const [userIdx, setUserId] = React.useState<Array<string>>([]);

	// 🔥 REFS
	const titleRef = React.useRef<HTMLInputElement | null>(null);
	const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);

	// 🔥 ACTIVE FIELD
	const [activeField, setActiveField] = React.useState<"title" | "body">("body");

	const [pushNotification, { isLoading: isDisabled }] =
		usePushNotificationMutation();
	const [uploadImage] = useUploadImageMutation();

	const [isFilterOpen, setIsFilterOpen] = React.useState(false);
	const [filterState, setFilterState] = React.useState({});
	const [image, setImage] = React.useState<any>(null);

	const handleFilterModal = () => setIsFilterOpen(!isFilterOpen);

	React.useEffect(() => {
		const id = userId?.filter((el: any) => el);
		setUserId(id);
	}, [userId]);

	// 🔥 UNIVERSAL INSERT FUNCTION
	const insertPlaceholder = (text: string) => {
		const input =
			activeField === "title" ? titleRef.current : bodyRef.current;

		if (!input) return;

		const start = input.selectionStart || 0;
		const end = input.selectionEnd || 0;

		const value =
			activeField === "title"
				? notificationPayload.title
				: notificationPayload.body;

		const newValue =
			value.substring(0, start) + text + value.substring(end);

		setNotificationPayload({
			...notificationPayload,
			[activeField]: newValue,
		});

		setTimeout(() => {
			input.selectionStart = input.selectionEnd = start + text.length;
			input.focus();
		}, 0);
	};

	const submitHandler = async (e: any) => {
		e.preventDefault();

		let bodyError = "";
		let titleError = "";

		if (!notificationPayload.title) titleError = "Please Enter a Title";
		if (!notificationPayload.body) bodyError = "Please enter the message";

		if (bodyError || titleError) {
			setNotificationPayload({ ...notificationPayload, bodyError, titleError });
			return;
		}

		const payload: any = {
			id: userIdx,
			body: notificationPayload.body,
			title: notificationPayload.title,
			target: artisan,
			filter: filterState,
			imageUrl: "",
		};

		try {
			if (image) {
				const formData = new FormData();
				formData.append("image", image);
				const res = await uploadImage(formData).unwrap();
				payload.imageUrl = res.payload.url;
			}

			const response = await pushNotification(payload).unwrap();
			toast.success(response?.message || "Notification sent successfully");

			handleClose();
		} catch (err: any) {
			toast.error("Error sending notification");
		}
	};

	return (
		<>
			<ToastContainer />

			<Modal open={open} onClose={handleClose} BackdropComponent={Backdrop}>
				<Fade in={open}>
					<Box sx={style}>
						<Wrapper>
							<div className='heading'>
								<Flex justify='space-between' style={{ width: "100%" }}>
									<p>New notification</p>
									<img src={closeIcon} onClick={handleClose} />
								</Flex>
							</div>

							<div className='content'>
								<Flex direction='column'>
									{/* ✅ KEEP FILTER */}
									{!userIdx?.length && (
										<Flex justify='space-between'>
											<div />
											<FilterButton onClick={handleFilterModal}>
												<img src={filterIcon} width={20} />
												Filter
											</FilterButton>
										</Flex>
									)}

									{/* 🔥 PLACEHOLDERS */}
									<div className='placeholder-buttons'>
										<button onClick={() => insertPlaceholder("{firstName}")}>
											Firstname
										</button>
										<button onClick={() => insertPlaceholder("{lastName}")}>
											Lastname
										</button>
										<button onClick={() => insertPlaceholder("{fullName}")}>
											Fullname
										</button>
									</div>

									{/* TITLE */}
									<p className='label'>Title</p>
									<input
										ref={titleRef}
										value={notificationPayload.title}
										onFocus={() => setActiveField("title")}
										onChange={(e) =>
											setNotificationPayload({
												...notificationPayload,
												title: e.target.value,
												titleError: "",
											})
										}
									/>
									{notificationPayload.titleError && (
										<p className='error'>{notificationPayload.titleError}</p>
									)}

									{/* BODY */}
									<p className='label'>Notification</p>
									<textarea
										ref={bodyRef}
										value={notificationPayload.body}
										onFocus={() => setActiveField("body")}
										onChange={(e) =>
											setNotificationPayload({
												...notificationPayload,
												body: e.target.value,
												bodyError: "",
											})
										}
									/>
									{notificationPayload.bodyError && (
										<p className='error'>{notificationPayload.bodyError}</p>
									)}

									{/* IMAGE */}
									<p className='label'>Image</p>
									<input
										type='file'
										onChange={(e: any) => setImage(e.target.files[0])}
									/>

									<Flex justify='flex-end'>
										<Button
											classes={[ButtonClass.SOLID]}
											onClick={submitHandler}
											disabled={isDisabled}
											style={{ backgroundColor: theme.colors.purple }}
										>
											{isDisabled ? (
												<Loading color='white' />
											) : (
												"Send notification"
											)}
										</Button>
									</Flex>
								</Flex>
							</div>
						</Wrapper>
					</Box>
				</Fade>
			</Modal>

			{/* ✅ KEEP FILTER MODAL */}
			{!userIdx?.length && (
				<FilterNotificationModal
					setFilterState={setFilterState}
					open={isFilterOpen}
					handleClose={handleFilterModal}
					user={artisan}
				/>
			)}
		</>
	);
};

export default SendNotificationModal;